import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { isAxiosApiError } from "@base/lib/utils";
import { useProjectAnalyticsGroup } from "@base/model/hooks";
import { useCurrentUserQuery } from "@base/model/hooks/useCurrentUserQuery";
import { dailyLogDetailToFormSchema } from "@features/daily-log-detail/model/schema";
import { dailyLogDetailQueryOptions } from "@features/daily-log-detail/model/useDailyLogDetailQuery";
import { useAcquireLockMutation } from "@features/daily-log-detail/model/useDailyLogLockMutations";
import { useDailyLogLockStatusQuery } from "@features/daily-log-detail/model/useDailyLogLockStatusQuery";
import { useLockManager } from "@features/daily-log-detail/model/useLockManager";
import { invalidateDailyLogSearch } from "@features/project-detail/model/dailyLogSearchQueries";
import { WriteDailyLogFunnel } from "@features/write-daily-log";
import { useDailyLogForm } from "@features/write-daily-log/model/form";
import type { DailyLogFormValues } from "@features/write-daily-log/model/schema";
import { useDailyLogWriteTracker } from "@features/write-daily-log/model/useDailyLogWriteTracker";
import { useEditDailyLogMutation } from "@features/write-daily-log/model/useEditDailyLogMutation";
import { SubmittingOverlay } from "@features/write-daily-log/ui/SubmittingOverlay";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate, useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/projects/$projectId/log/$logId/edit")({
  component: EditDailyLogPage,
});

function EditDailyLogPage() {
  const { projectId, logId } = Route.useParams();

  return <EditDailyLogPageContent projectId={projectId} logId={logId} />;
}

interface EditDailyLogPageContentProps {
  projectId: string;
  logId: string;
}

function EditDailyLogPageContent({ projectId, logId }: EditDailyLogPageContentProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // 상세에서 받은 검색 목록 복귀 정보. 저장 후 상세로 돌아갈 때 이어 줘야 목록 복귀가 유지된다.
  const dailyLogSearchReturn = useRouterState({ select: (state) => state.location.state.dailyLogSearchReturn });

  const tracker = useDailyLogWriteTracker({ projectId, mode: "edit" });

  useProjectAnalyticsGroup(projectId);

  // lock 상태 확인 (마운트 시 최신값 조회)
  const { data: lockStatus } = useDailyLogLockStatusQuery(projectId, logId);
  const { data: currentUserData } = useCurrentUserQuery();
  const currentUserId = currentUserData?.user.id;

  // 타인이 lock을 보유하고 있으면 상세 페이지로 리다이렉트
  useEffect(() => {
    if (!lockStatus || currentUserId === undefined) return;
    if (lockStatus.locked && lockStatus.modifier.id !== currentUserId) {
      toast.warning("Someone is currently editing this daily log.");
      EventLogger.result(CLIENT_EVENT.DAILY_LOG_LOCK_CONFLICT, {
        project_id: projectId,
        log_id: logId,
        source: "edit_route",
        error_code: null,
      });
      // 사용자가 포기한 게 아니라 앱이 내보낸 것이다. 이탈 분포에 섞이면
      // "Overview 에서 가장 많이 포기한다"는 잘못된 결론이 나온다.
      tracker.markRedirected();
      navigate({
        to: "/projects/$projectId/log/$logId",
        params: { projectId, logId },
        replace: true,
        state: { dailyLogSearchReturn },
      });
    }
  }, [lockStatus, currentUserId, navigate, projectId, logId, tracker.markRedirected, dailyLogSearchReturn]);

  // 직접 URL 접근 또는 새로고침: lock 미보유 시 획득 시도 (1회만 실행)
  const { mutate: acquireLock } = useAcquireLockMutation(projectId, logId, {
    onError: (err) => {
      if (isAxiosApiError(err)) {
        if (err.response.data.code === "EXISTING_LOCK") {
          toast.warning("Someone is currently editing this daily log.");
        } else if (err.response.data.code === "NONEXISTENT_DATA") {
          toast.warning("The daily log could not be found.");
        } else {
          toast.warning("Failed to lock. Please try again in a moment.");
        }
      } else {
        toast.warning("Failed to lock. Please try again in a moment.");
      }

      // 실제 경합일 때만 보낸다. 락 획득은 네트워크·서버·데이터 오류로도 실패하는데,
      // 그것까지 경합으로 세면 "동시 편집 빈도"가 회선 품질에 따라 부풀려진다.
      // 경합이 아닌 실패는 `api_error` 가 엔드포인트 단위로 이미 집계한다.
      if (isAxiosApiError(err) && err.response.data.code === "EXISTING_LOCK") {
        EventLogger.result(CLIENT_EVENT.DAILY_LOG_LOCK_CONFLICT, {
          project_id: projectId,
          log_id: logId,
          source: "edit_route",
          error_code: "EXISTING_LOCK",
        });
      }

      tracker.markRedirected();
      navigate({
        to: "/projects/$projectId/log/$logId",
        params: { projectId, logId },
        replace: true,
        state: { dailyLogSearchReturn },
      });
    },
  });

  const acquireAttemptedRef = useRef(false);

  useEffect(() => {
    if (acquireAttemptedRef.current) return;
    if (!lockStatus || currentUserId === undefined) return;

    acquireAttemptedRef.current = true;

    if (!lockStatus.locked) {
      acquireLock();
    }
    // lockStatus.locked && modifier.id === currentUserId → 이미 보유, heartbeat만 시작
  }, [lockStatus, currentUserId, acquireLock]);

  // heartbeat + 페이지 종료 시 best-effort 해제
  const { isConnectionUnstable, releaseLock } = useLockManager(projectId, logId);

  const { data, isLoading } = useQuery(dailyLogDetailQueryOptions(projectId, logId));

  // onSuccess 콜백에는 제출한 폼 값이 전달되지 않으므로 직전 제출값을 보관한다.
  const submittedValuesRef = useRef<DailyLogFormValues | null>(null);

  const defaultValues = useMemo(() => (data ? dailyLogDetailToFormSchema.parse(data) : undefined), [data]);

  const initialImageMap = useMemo(
    () => new Map(data?.images.map((img) => [img.webp_image_upload_path ?? img.image_upload_path, img.id]) ?? []),
    [data?.images],
  );

  const { mutate: editDailyLog, isPending: isSubmitting } = useEditDailyLogMutation(projectId, logId, {
    onSuccess: () => {
      if (submittedValuesRef.current) tracker.trackSubmitSuccess(submittedValuesRef.current);
      toast.success("Daily log updated successfully");
      queryClient.invalidateQueries({
        queryKey: dailyLogDetailQueryOptions(projectId, logId).queryKey,
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: ["documents", "infinite", Number(projectId)], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["documents", projectId], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["comments", projectId, logId], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["file-history", projectId, logId], refetchType: "all" });
      // 본문·날짜·업체·태그가 바뀌었을 수 있다. 목록은 복귀할 때 한 번 갱신된다.
      invalidateDailyLogSearch(queryClient, projectId);
      releaseLock();
      navigate({
        to: "/projects/$projectId/log/$logId",
        params: { projectId, logId },
        replace: true,
        state: { dailyLogSearchReturn },
      });
    },
    onError: (err) => {
      tracker.trackSubmitFail({ reason: "api", errorCode: isAxiosApiError(err) ? err.response.data.code : undefined });

      if (isAxiosApiError(err)) {
        if (err.response.data.code === "DAILY_LOG_ALREADY_SUBMITTED") {
          toast.error("Daily Log for today already exists");
          return;
        }
      }
      toast.error("Failed to update daily log");
    },
  });

  const handleSave = (values: DailyLogFormValues) => {
    tracker.trackSubmitStart();
    submittedValuesRef.current = values;

    const existingIds: number[] = [];
    const created: string[] = [];

    for (const url of values.image_urls) {
      const id = initialImageMap.get(url);
      if (id !== undefined) {
        existingIds.push(id);
      } else {
        created.push(url);
      }
    }

    editDailyLog({
      description: values.description,
      location: values.location,
      date: values.date,
      weather: values.weather,
      temperature: values.temperature.toString(),
      temperature_unit: "F",
      precipitation: values.precipitation.toString(),
      precipitation_unit: "MM",
      process_rate: values.processRate.toString(),
      tags: values.tags,
      workforces: values.workforce_contractors.flatMap((c) =>
        c.workforces.map((w) => ({ contractor_id: c.id, trade_id: Number(w.id), quantity: w.quantity })),
      ),
      equipments: values.equipment_contractors.flatMap((c) =>
        c.equipments.map((e) => ({ contractor_id: c.id, trade_id: Number(e.id), quantity: e.quantity })),
      ),
      images: { existing_ids: existingIds, created },
    });
  };

  const form = useDailyLogForm({ onSubmit: handleSave, defaultValues });

  const handleNavigateBack = () => {
    releaseLock();
    queryClient.invalidateQueries({
      queryKey: dailyLogDetailQueryOptions(projectId, logId).queryKey,
      refetchType: "all",
    });
    queryClient.invalidateQueries({ queryKey: ["documents", "infinite", Number(projectId)], refetchType: "all" });
    queryClient.invalidateQueries({ queryKey: ["documents", projectId], refetchType: "all" });
    router.history.back();
  };

  return (
    <div className="relative flex-1">
      {isConnectionUnstable && (
        <div className="bg-yellow-50 px-3 py-2 text-center text-label3 text-yellow-700">
          Connection unstable. Your lock may expire soon.
        </div>
      )}
      <WriteDailyLogFunnel
        form={form}
        projectId={projectId}
        isInitialSyncPending={isLoading}
        onNavigateBack={handleNavigateBack}
        onStepChange={tracker.trackStepView}
        onSubmit={() => form.handleSubmit()}
      />
      {isSubmitting && <SubmittingOverlay />}
    </div>
  );
}
